import { useCallback, useEffect, useRef, useState } from 'react';
import { useSocket, useSocketEvent } from 'socket.io-react-hook';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import { _getToken } from 'src/Config/axiosApi';
import { refreshAuthToken } from 'src/Common/context/AuthContext';
import { NotificationEvent } from '../types/notifications.types';

/**
 * Cooldown between auto-refresh attempts on a connect/auth error. Without
 * a guard, a stale token that keeps getting rejected on every reconnect
 * attempt would hammer `/auth/refresh` in a tight loop. One refresh per
 * window is enough to recover; subsequent errors inside the window just
 * surface (toast + Sentry) without re-refreshing.
 */
const AUTH_RECOVERY_COOLDOWN_MS = 10_000;

/**
 * Connects to the notifications-websocket service and exposes a
 * stream of new `NotificationEvent`s. The page consumes these and
 * prepends them to its react-query infinite list.
 *
 * Auth: the same JWT pattern as `useUnwindingSocket` —
 * `socket.io-client` reads the current token from a callback so
 * silent refresh / logout propagate without a manual reconnect.
 *
 * Connection model: separate Socket.io connection from `/unwind`
 * (different host) — by design. Notification fanout cannot
 * head-of-line block the market-data WS this way.
 */
export function useNotificationsSocket() {
  const wsUrl = import.meta.env.VITE_NOTIFICATIONS_WS_URL;
  const accessToken = _getToken();

  const { socket } = useSocket(wsUrl || '', {
    enabled: !!wsUrl && !!accessToken,
    transports: ['websocket'],
    auth: (cb: (data: { token: string }) => void) =>
      cb({ token: _getToken() }),
  });

  const [latestEvent, setLatestEvent] = useState<NotificationEvent | null>(
    null,
  );
  const [bufferedCount, setBufferedCount] = useState(0);
  /**
   * Bumps every time the socket transitions disconnected → connected.
   * The page reads this to refetch its REST history on reconnect:
   * Redis pub/sub is fire-and-forget, so any event published while
   * the gateway pod was restarting (or the browser was sleeping) is
   * lost on the realtime channel. Mongo persistence is the durable
   * record — refetching the latest page closes the gap.
   *
   * Increments on EVERY reconnect, not just the first, so a long
   * tab-sleep + wake cycle re-pages too.
   */
  const [reconnectCount, setReconnectCount] = useState(0);
  const hasSubscribed = useRef(false);
  // Tracks whether we've ever observed a disconnect on this socket
  // instance — so the very first `connect` after mount doesn't bump
  // the reconnect counter (initial mount is not a reconnect).
  const hasDisconnected = useRef(false);
  // Last time we auto-refreshed the token in response to a connect/auth
  // error — throttles the refresh+reconnect so a persistently-rejected
  // token can't loop on `/auth/refresh`.
  const lastAuthRecoveryAt = useRef(0);

  // Subscribe on every (re)connect.
  useEffect(() => {
    const subscribe = () => {
      if (!hasSubscribed.current) {
        socket.emit('subscribe_notifications');
        hasSubscribed.current = true;
      }
      if (hasDisconnected.current) {
        // Genuine reconnect — bump the counter so the page refetches.
        setReconnectCount((n) => n + 1);
        hasDisconnected.current = false;
      }
    };
    const reset = () => {
      hasSubscribed.current = false;
      hasDisconnected.current = true;
    };

    socket.on('connect', subscribe);
    socket.on('disconnect', reset);
    if (socket.connected) subscribe();
    else reset();

    return () => {
      socket.off('connect', subscribe);
      socket.off('disconnect', reset);
    };
  }, [socket, socket.connected]);

  // JWT-expiry recovery. The gateway disconnects a socket whose token
  // expired (`auth_error` + `disconnect(true)`); socket.io surfaces this
  // as `connect_error` on the next (re)connect attempt. Without a handler
  // the socket silently stayed down — no toast, no token refresh, no
  // reconnect with a fresh token ("dejé de ver las notis al rato"). On
  // either error we refresh the token ONCE per cooldown window (reusing
  // the shared `refreshAuthToken`, which updates storage) and force a
  // reconnect so the `auth` callback re-reads the fresh token. Always
  // surface the failure (toast + Sentry) so it's never silent.
  useEffect(() => {
    const handleAuthError = async (err?: unknown) => {
      const now = Date.now();
      if (now - lastAuthRecoveryAt.current < AUTH_RECOVERY_COOLDOWN_MS) {
        // Inside the cooldown — surface but don't loop on refresh.
        Sentry.captureException(
          err instanceof Error ? err : new Error('notifications WS auth error'),
          { tags: { area: 'notifications-ws' } },
        );
        return;
      }
      lastAuthRecoveryAt.current = now;

      let newToken: string | null = null;
      try {
        newToken = await refreshAuthToken();
      } catch (refreshErr) {
        Sentry.captureException(refreshErr, {
          tags: { area: 'notifications-ws' },
        });
      }

      if (newToken) {
        // Force a reconnect so the `auth` callback re-reads the fresh
        // token. `connect()` is a no-op if already connecting.
        try {
          socket.disconnect();
        } catch {
          /* already closed */
        }
        socket.connect();
      } else {
        toast.error(
          'Notifications disconnected — please refresh the page to sign in again.',
          { toastId: 'notifications-ws-auth' },
        );
        Sentry.captureException(
          err instanceof Error
            ? err
            : new Error('notifications WS auth error — refresh failed'),
          { tags: { area: 'notifications-ws' } },
        );
      }
    };

    socket.on('connect_error', handleAuthError);
    socket.on('auth_error', handleAuthError);
    return () => {
      socket.off('connect_error', handleAuthError);
      socket.off('auth_error', handleAuthError);
    };
  }, [socket]);

  const { lastMessage } = useSocketEvent<NotificationEvent>(
    socket,
    'notification',
  );

  useEffect(() => {
    if (!lastMessage) return;
    setLatestEvent(lastMessage);
    setBufferedCount((n) => n + 1);
  }, [lastMessage]);

  /** Page calls this after consuming `latestEvent` so the same event
   *  isn't re-applied on re-renders. */
  const acknowledge = useCallback(() => {
    setLatestEvent(null);
  }, []);

  /** Reset the buffered count badge (e.g. after the page refocuses). */
  const resetBufferedCount = useCallback(() => {
    setBufferedCount(0);
  }, []);

  return {
    isConnected: socket.connected,
    latestEvent,
    bufferedCount,
    /**
     * Counter that increments each time the socket transitions
     * disconnected → connected. The page subscribes to it as a
     * react-query refetch trigger so any event missed during the
     * disconnect (Redis pub/sub is fire-and-forget) gets picked up
     * from Mongo via REST.
     */
    reconnectCount,
    acknowledge,
    resetBufferedCount,
  };
}
